import { useCallback, useState } from 'react'
import type { RouterInputs, RouterOutputs } from '@klear/ui/lib/trpc'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'
import constant from 'lodash/constant'

type CreditCreateInput = RouterInputs['credit']['create']
type CreditUpdateInput = RouterInputs['credit']['update']
type CreditRefreshAllianzDataInput = RouterInputs['credit']['refreshAllianzData']
type CreditMutationResult = RouterOutputs['credit']['create']

const invalidateQueries = ['credit.get', 'credit.getByCustomer', 'credit.list', 'customer.get', 'customer.list']

export function useCredit(organizationId: number | undefined, options?: { enabled?: boolean }) {
  const {
    data: credit,
    error,
    isLoading
  } = trpc.credit.get.useQuery(
    { organizationId: organizationId ?? 0 },
    {
      enabled: (options?.enabled ?? true) && organizationId != null && organizationId > 0
    }
  )

  return { credit, error, isLoading }
}

export function useCreditMutations() {
  const utils = trpc.useUtils()
  const [isPreviewing, setIsPreviewing] = useState(false)
  const createMutation = useMutationWithToast(
    trpc.credit.create.useMutation,
    createToastConfig<CreditMutationResult>('Credit', 'create', constant('Credit')),
    { invalidateQueries }
  )
  const updateMutation = useMutationWithToast(
    trpc.credit.update.useMutation,
    createToastConfig<CreditMutationResult>('Credit', 'update', constant('Credit')),
    { invalidateQueries }
  )
  const refreshAllianzMutation = useMutationWithToast(
    trpc.credit.refreshAllianzData.useMutation,
    createToastConfig<CreditMutationResult>('Allianz credit', 'refresh'),
    { invalidateQueries }
  )

  const previewNewCredit = useCallback(
    async (input: CreditCreateInput): Promise<CreditMutationResult> => {
      setIsPreviewing(true)
      try {
        return await utils.credit.preview.fetch({
          organizationId: input.organizationId,
          data: input.data
        })
      } finally {
        setIsPreviewing(false)
      }
    },
    [utils]
  )

  const previewCredit = useCallback(
    async (input: CreditUpdateInput): Promise<CreditMutationResult> => {
      setIsPreviewing(true)
      try {
        return await utils.credit.preview.fetch({
          organizationId: input.organizationId,
          data: input.data,
          patch: true
        })
      } finally {
        setIsPreviewing(false)
      }
    },
    [utils]
  )

  const commitNewCredit = useCallback(
    async (input: CreditCreateInput): Promise<CreditMutationResult | null> => {
      return createMutation.mutateAsync(input)
    },
    [createMutation.mutateAsync]
  )

  const commitCredit = useCallback(
    async (input: CreditUpdateInput): Promise<CreditMutationResult | null> => {
      return updateMutation.mutateAsync(input)
    },
    [updateMutation.mutateAsync]
  )

  const refreshAllianzData = useCallback(
    async (input: CreditRefreshAllianzDataInput): Promise<CreditMutationResult | null> => {
      return refreshAllianzMutation.mutateAsync(input)
    },
    [refreshAllianzMutation.mutateAsync]
  )

  return {
    previewNewCredit,
    previewCredit,
    commitNewCredit,
    commitCredit,
    refreshAllianzData,
    isPreviewing,
    isRefreshingAllianz: refreshAllianzMutation.isPending,
    isCommitting: createMutation.isPending || updateMutation.isPending
  }
}
